import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react'
import {
  fetchGameBalanceAll,
  fetchGameHall,
  fetchGameListV1,
  fetchGameMarquee,
  fetchGameUserStatus,
  postMemberGameClick,
} from 'api/index'

import {
  Wrapper,
  GameList,
  GoTop,
  LobbyTitle,
  PageWrapper,
  LobbyErrorMessage,
  GamePanelSide,
  GamePanelCenter,
  GamePanel,
  GameCategoryIcon,
  StyledEmptyResult,
  StyledColoredIcon,
  GameListWrap,
  LoadingOverlay,
  StyledAnimation,
  GamePanelCenterTabGamesMono,
  TransferButton,
  MarqueeRow,
} from './Styled'

import Jumbotron from './component/Jumbotron'
import BalanceBar from './component/BalanceBar'
import GameIcon from './component/GameIcon'
import Marquee from './component/Marquee'
import Loading from './component/Loading'
import { useOpenGamePopup } from 'hooks/useOpenGamePopup'
import { useToastAlert } from 'hooks/useToastAlert'
import UsePageVisible from 'page/Profile/hooks/usePageVisible'
import { PrimaryButton } from 'commonStyledComponents'
import { useLocation } from 'react-router-dom'
import { useNavigateTo } from 'hooks/useNavigateTo'
import useConfirmModal from 'hooks/useConfirmModal'
import { CONFIRM_MODAL_TYPE } from 'component/Modal/ConfirmModal'
import { useMutex } from 'hooks/useMutex'
import { GAME_CATEGORY_LIST, GAME_MESSAGE } from 'constant/game'
import { useDispatch, useSelector } from 'react-redux'
import { selectGameBalance, selectGameUserStatus } from 'redux/selector/game'
import actions from 'redux/action'
import { selectUserInfo } from 'redux/selector/user'
import { useGameBanCheck } from 'hooks/useGameBanCheck'
import { IP_BAN_ERROR_CODE, useIpBanCheck } from 'hooks/useIpBanCheck'
import CustomerServiceModal from 'component/CustomerServiceModal'

const subsets = GAME_CATEGORY_LIST

export default function GameLobby() {
  const [banner, setBanner] = useState<any[]>([])
  const [marquee, setMarquee] = useState<any[]>([])
  const [csList, setCsList] = useState<any[]>([])
  const userInfo = useSelector(selectUserInfo)
  const [lobbyError, setLobbyError] = useState<boolean>(false)
  const [lobbyErrorMessage, setLobbyErrorMessage] = useState<string>('')
  const [errorRetryButtonText, setErrorRetryButtonText] = useState<string>('確定')
  const [isCsModalOpen, setCsModalOpen] = useState<boolean>(false)
  const uid = useMemo(() => (userInfo ? formatUid(userInfo.show_id) : ''), [userInfo])
  const [scrollTopVisible, setScrollTopVisible] = useState<boolean>(false)
  const { navigateToGameTransfer, navigateToGameDeposit, navigateToGameWithdraw } = useNavigateTo()
  const openGamePopup = useOpenGamePopup()
  // const closeGamePopup = useCloseGamePopup()
  const { toast } = useToastAlert()
  const { openConfirm, formatApiErrorMessage, formatGameLobbyApiErrorButtonText } = useConfirmModal()
  const [currentSubsetState, setCurrentSubsetState] = useState<{ manual: boolean; value: string }>({
    manual: true,
    value: subsets[0].name,
  })
  const [visibleSubset, setVisibleSubset] = useState<any[]>([])
  const [pageInit, setPageInit] = useState<boolean>(false)
  const pageLoadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // const [platformList, setPlatformList] = useState(
  //   /** @type { Awaited<ReturnType<typeof fetchGamePlatformList>>['data'] } */ ([])
  // )

  const dispatch = useDispatch()
  const userStatus = useSelector(selectGameUserStatus)
  const balance = useSelector(selectGameBalance)

  const { isVisible } = UsePageVisible()
  const currentLocation = useLocation()
  const { wrap: customerServiceMutex } = useMutex()
  const { wrap: promotionMutex } = useMutex()

  const openCsModal = useCallback(() => {
    if (csList.length > 0) setCsModalOpen(true)
    else toast('目前暂无客服人员')
  }, [csList.length, toast])

  const { validateUser } = useGameBanCheck(userStatus)
  const { validateIpBan, updateIsIpBanStatus } = useIpBanCheck()

  const validateGameAccess = useCallback(
    (callback?: () => unknown) => validateIpBan(() => validateUser(callback)),
    [validateIpBan, validateUser]
  )

  let balanceLoadTime = useRef<number>(0)

  // the following effect is used for fetching platform list, but we don't need platform list currently.
  // useEffect(() => {
  //   const controller = new AbortController()
  //   const signal = controller.signal
  //   fetchGamePlatformList({ signal }).then((res) => {
  //     setPlatformList(res.data)
  //     setCurrentPlatform(res.data[0].tp_code)
  //   })

  //   return () => {
  //     controller.abort()
  //   }
  // }, [])

  // const [currentPlatform, setCurrentPlatform] = useState(null)

  const [currentGameState, setCurrentGameState] = useState<{
    params: any
    requireFetching: boolean
    loading: boolean
    initialLoaded: boolean
    current: number
    hasMore: boolean
    games: any[]
  }>({
    params: null,
    requireFetching: true,
    loading: false,
    initialLoaded: false,
    current: 1,
    hasMore: true,
    games: [],
  })

  const [groupedGames, setGroupedGames] = useState<any[]>([])

  const loadUserStatus = useCallback(async () => {
    try {
      const status = await fetchGameUserStatus()
      dispatch(actions.updateGameUserStatus(status))
    } catch (ex) {
      console.warn(ex)
    }
  }, [dispatch])

  const loadBalance = useCallback(
    async function loadBalance({
      onSuccess,
      onError,
      silence,
    }: { onSuccess?: () => void; onError?: (ex: any) => void; silence?: boolean } = {}) {
      let isOk = false
      try {
        if (Date.now() - balanceLoadTime.current < 500) return
        balanceLoadTime.current = Date.now()
        const data = await fetchGameBalanceAll()
        dispatch(actions.updateGameBalance(data?.total || 0))
        isOk = true
      } catch (ex) {
        if (typeof onError === 'function') {
          onError(ex)
        }
        if (!silence) {
          toast('暂时无法取得余额')
        }
        console.warn(formatApiErrorMessage(ex))

        return
      }
      if (typeof onSuccess === 'function' && isOk) {
        onSuccess()
      }
    },
    [dispatch, formatApiErrorMessage, toast]
  )

  const onClickLoadBalance = useCallback(() => {
    return loadBalance()
  }, [loadBalance])

  const loadSystemInfo = useCallback(
    async function loadSystemInfo(silence?: boolean) {
      try {
        const data = await fetchGameMarquee()
        setBanner([...data.banner])
        setMarquee([...data.marquee])
        setCsList([...data.customerServiceUrl])
      } catch (ex) {
        if (!silence) {
          const code = (ex as unknown as { code: number }).code
          if (code === IP_BAN_ERROR_CODE) {
            updateIsIpBanStatus(true)
          } else {
            openConfirm({
              message: formatApiErrorMessage(ex),
              type: CONFIRM_MODAL_TYPE.OK,
            })
          }
        }
        console.warn(ex)
      }
    },
    [formatApiErrorMessage, openConfirm]
  )

  const loadInfo = useCallback(
    async (silence?: boolean) => {
      // 順序會影響第一次進入遊戲的用戶註冊
      await loadSystemInfo(silence)
      await loadUserStatus()
      loadBalance({ silence })
    },
    [loadSystemInfo, loadUserStatus, loadBalance]
  )

  const handleEnterGame = useCallback(
    function handleEnterGame(game: any) {
      validateGameAccess(() => {
        openGamePopup({
          id: game.gameId,
          gameType: game.gameType,
          tpCode: game.tpCode,
          direction: game.direction,
        })
      })
    },
    [openGamePopup, validateGameAccess]
  )

  function toGameDeposit() {
    validateGameAccess(() => {
      // vertical mode
      navigateToGameDeposit({fromGameDirection: 1})
    })
  }

  function toGameWithdraw() {
    validateGameAccess(() => {
      navigateToGameWithdraw({})
    })
  }

  const toGamePromotion = promotionMutex(() => {
    toast(GAME_MESSAGE.NOT_RELEASE)
    // navigateToGamePromotion({})
    return new Promise<void>((res) => {
      setTimeout(() => {
        res()
      }, 2000)
    })
  })

  function toGameTransfer() {
    validateGameAccess(() => {
      navigateToGameTransfer({})
    })
  }

  const openCsPage = customerServiceMutex((item: any) => {
    setCsModalOpen(false)
    if (item) {
      window.open(item.url)
    } else {
      toast(GAME_MESSAGE.NOT_RELEASE)
      console.warn('no customer service available')
    }
    return new Promise<void>((res) => {
      setTimeout(() => {
        res()
      }, 2000)
    })
  })

  function formatUid(uid: string) {
    if (!uid) return ''
    return uid.replace('-', '').toLowerCase()
  }

  useEffect(() => {
    // 當畫面回到lobby時，重新載入資料
    if (!pageInit) {
      // 第一次載入
      setPageInit(true)
      loadInfo()
    } else if (isVisible) {
      // 再次載入延遲兩秒以避免safari的sw異常行為
      if(pageLoadTimeoutRef.current) {
        clearTimeout(pageLoadTimeoutRef.current)
      }
      pageLoadTimeoutRef.current = setTimeout(() => {
        loadInfo(true)
        pageLoadTimeoutRef.current = null
      }, 2000)
    }
  }, [loadInfo, isVisible, pageInit])

  useEffect(() => {
    // navigate到本頁面時，重置balance
    setTimeout(() => {
      if (currentLocation?.pathname === '/game') loadBalance({ silence: true })
    }, 2000)
  }, [currentLocation, loadBalance])

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal
    const load = async () => {
      if (!currentGameState.requireFetching) {
        return
      }

      if (!currentGameState.loading) {
        setCurrentGameState((state) => ({ ...state, loading: true }))
        return
      }

      setLobbyError(false)
      setLobbyErrorMessage('')

      try {
        // group
        const hall = await fetchGameHall()

        const filteredSubset = subsets.filter((m) =>
          hall.game_type.find((n: any) => !m.filter.gameType || m.filter.gameType === n)
        )
        setVisibleSubset(filteredSubset)
        if (filteredSubset.length && filteredSubset.find((m) => m.name !== currentSubsetState.value)) {
          setCurrentSubsetState({
            value: filteredSubset[0].name,
            manual: false,
          })
        }
        // list
        const listRes = await fetchGameListV1({
          pageSize: 1000,
          current: currentGameState.current,
        })
        if (signal.aborted) {
          return
        }

        const games = [...currentGameState.games, ...(listRes?.data ?? [])]
        setCurrentGameState((state) => ({
          ...state,
          loading: false,
          initialLoaded: true,
          hasMore: listRes?.page_result.pageSize * listRes?.page_result.current < listRes?.page_result.total,
          current: state.current + 1,
          requireFetching: false,
          games: games,
        }))

        const grouped = subsets.map((i) => ({
          name: i.name,
          icon: i.icon,
          games: games
            .filter((g) => {
              const rule = i.filter
              for (const [k, v] of Object.entries(rule)) {
                if (g[k] !== v) return false
              }
              return true
            })
            .map((m, i) => (
              <GameIcon
                key={'game_icon_' + i}
                icon={m.imgUrl}
                name={m.gameName}
                disabled={m.isUnderMaintain === 1}
                onClick={() => handleEnterGame(m)}
              ></GameIcon>
            )),
        }))
        setGroupedGames(grouped)
      } catch (ex) {
        if (signal.aborted) {
          return
        }
        const message = formatApiErrorMessage(ex)
        const code = (ex as unknown as { code: number }).code
        if (code === IP_BAN_ERROR_CODE) {
          updateIsIpBanStatus(true)
        } else {
          openConfirm({
            message: message,
            type: CONFIRM_MODAL_TYPE.OK,
          })
        }
        console.warn(ex)
        setLobbyError(true)
        setLobbyErrorMessage(message)
        setErrorRetryButtonText(formatGameLobbyApiErrorButtonText(ex))
        setCurrentGameState((state) => ({
          ...state,
          requireFetching: false,
          initialLoaded: true,
          loading: false,
          hasMore: false,
        }))
      }
    }

    load()

    return () => {
      controller.abort()
    }
  }, [
    currentGameState,
    currentSubsetState.value,
    formatApiErrorMessage,
    formatGameLobbyApiErrorButtonText,
    handleEnterGame,
    openConfirm,
  ])

  const handleFetchGame = () => {
    setCurrentGameState((v) => {
      if (v.requireFetching) return v
      return {
        ...v,
        requireFetching: true,
      }
    })
  }

  // create a platform options
  // const platformOptions = useMemo(() => {
  //   return platformList.map((i) => ({ id: i.tp_code, name: i.name }))
  // }, [platformList])

  // const showPlatformOptions = useMemo(() => platformOptions.length > 1, [platformOptions])

  const pageRef = useRef<HTMLDivElement>(null)
  const tabHeadAnchorRef = useRef<HTMLDivElement>(null)
  const tabsHeadRef = useRef<HTMLDivElement>(null)

  const [maxSidebarHeight, setMaxSidebarHeight] = useState<number | null>(null)

  useEffect(() => {
    if (pageRef.current == null || tabsHeadRef.current == null) {
      return
    }
    /**
     * @type {HTMLDivElement}
     */
    const pageEl = pageRef.current
    /**
     * @type {HTMLDivElement}
     */
    const tabsEl = tabsHeadRef.current
    const obs = new ResizeObserver(() => {
      const wrapperSize = pageEl.getBoundingClientRect()
      const headSize = tabsEl.getBoundingClientRect()
      setMaxSidebarHeight(wrapperSize.height - headSize.height)
    })
    obs.observe(pageRef.current)

    return () => {
      obs.disconnect()
    }
  }, [])

  const [fakeLoadingState, setFakeLoadingState] = useState<'init' | 'running'>('init')

  const resetScroll = () => {
    if(pageRef.current == null || tabHeadAnchorRef.current == null) {
      return
    }
    const initial = pageRef.current.scrollTop
    const target = tabHeadAnchorRef.current.offsetTop - pageRef.current.offsetTop

    if (initial > target) {
      pageRef.current.scrollTop = target
    }
  }

  const onChangeSubset = (s: string) => {
    setFakeLoadingState('running')
    setCurrentSubsetState({
      value: s,
      manual: false,
    })
    resetScroll()
  }

  function handleToTop() {
    pageRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    if (fakeLoadingState === 'init') return

    const id = setTimeout(() => {
      setFakeLoadingState('init')
    }, 200)

    return () => clearTimeout(id)
  }, [fakeLoadingState])

  // const [mainPanelDocked, setMainPanelDocked] = useState()

  const onPageScroll = () => {
    const anchor = pageRef.current?.scrollTop ?? 0
    // const target = tabHeadAnchorRef.current.offsetTop - pageRef.current.offsetTop

    // if (anchor >= target) {
    //   setMainPanelDocked(true)
    // } else {
    //   setMainPanelDocked(false)
    // }
    if (anchor > 100) setScrollTopVisible(true)
    else setScrollTopVisible(false)
  }

  useEffect(() => {
    const flagKey = 'gameLobbyFirstEnter'
    if (!sessionStorage.getItem(flagKey)) {
      // 第一次進入，需要觸發進入大廳的紀錄事件，這個事件只會在該次瀏覽期間觸發一次
      sessionStorage.setItem(flagKey, '1')
      try {
        postMemberGameClick()
      } catch (ex) {
        console.log('unable to log lobby click')
      }
    }
  }, [])

  return (
    <PageWrapper>
      <LobbyTitle>棋牌游戏</LobbyTitle>
      <Wrapper>
        {banner?.length > 0 && <Jumbotron list={banner}></Jumbotron>}
        <MarqueeRow>
          <Marquee list={marquee} speed={60}></Marquee>
          <TransferButton onClick={toGameTransfer}>
            <div>额度转换</div>
          </TransferButton>
        </MarqueeRow>
        <BalanceBar
          balance={balance}
          uid={uid}
          onRefresh={onClickLoadBalance}
          toGameDeposit={toGameDeposit}
          toGameWithdraw={toGameWithdraw}
          toGamePromotion={toGamePromotion}
          toService={openCsModal}
        ></BalanceBar>
        <div ref={tabHeadAnchorRef} style={/* workaround for leaking BalanceBar  */ { marginTop: '1px' }}></div>
        <GamePanel>
          <GamePanelSide
            style={{
              maxHeight: maxSidebarHeight ? `${maxSidebarHeight}px` : undefined,
              // overflowY: mainPanelDocked ? 'auto' : 'hidden',
              // top: showPlatformOptions ? 'var(--tab-bar-height)' : '0',
            }}
          >
            {visibleSubset.map((i, index) => {
              return (
                <GameCategoryIcon
                  key={'category_icon_' + index}
                  active={currentSubsetState.value === i.name}
                  onClick={() => onChangeSubset(i.name)}
                  data-subset-icon={i.name}
                >
                  <StyledColoredIcon src={i.icon} />
                  {i.name}
                </GameCategoryIcon>
              )
            })}
          </GamePanelSide>
          <GamePanelCenter ref={pageRef} onScroll={onPageScroll}>
            {lobbyError ? (
              <LobbyErrorMessage>
                <div>{lobbyErrorMessage}</div>
                <PrimaryButton onClick={handleFetchGame}>{errorRetryButtonText}</PrimaryButton>
              </LobbyErrorMessage>
            ) : (
              <GamePanelCenterTabGamesMono>
                <React.Fragment key={'game_list'}>
                  {fakeLoadingState === 'init' &&
                    groupedGames.map((s) =>
                      s.name === currentSubsetState.value ? (
                        <GameListWrap
                          key={s.name}
                          style={{
                            opacity: (fakeLoadingState as 'init' | 'running') === 'running' ? '0' : '1',
                          }}
                        >
                          {s.games.length !== 0 ? (
                            <GameList data-name={s.name}>{s.games}</GameList>
                          ) : (
                            <StyledEmptyResult></StyledEmptyResult>
                          )}
                        </GameListWrap>
                      ) : null
                    )}

                  {fakeLoadingState === 'running' && (
                    <GameListWrap>
                      <LoadingOverlay>
                        <StyledAnimation />
                      </LoadingOverlay>
                    </GameListWrap>
                  )}
                </React.Fragment>
              </GamePanelCenterTabGamesMono>
            )}
            <GoTop onClick={handleToTop} className={scrollTopVisible ? 'visible' : ''}></GoTop>
            {!currentGameState.initialLoaded ? (
              <Loading active={!currentGameState.initialLoaded}>加载中</Loading>
            ) : (
              <></>
            )}
          </GamePanelCenter>
        </GamePanel>
        <CustomerServiceModal
          isOpen={isCsModalOpen}
          onClose={() => setCsModalOpen(false)}
          list={csList}
          onClick={openCsPage}
        ></CustomerServiceModal>
      </Wrapper>
    </PageWrapper>
  )
}
